import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Col, Empty, Flex, Input, List, Modal, Row, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { getTicketsMyHandleForRef } from 'src/apis/helpdesk/ticket.api';
import {
  BusinessTripIcon,
  CartIcon,
  Expired,
  MaintenanceIcon,
  MoneyIcon,
  OvertimeIcon,
  TruckIcon,
  WorkInProcessIcon
} from 'src/assets/icons';
import { EmployeeGroupSelect } from 'src/components/v1/Select';
import { OpenRelatedTicketType } from 'src/components/v2/Button/BtnHandleList';
import CustomCard from 'src/components/v2/Card';
import Icon from 'src/components/v2/Icon';
import PriorityLevelTag from 'src/components/v2/Tag/PriorityLevelTag';
import { HrCategoryEn } from 'src/constants/hrCategory.enum';
import { ILinkId } from 'src/constants/linkId.enum';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { TicketDetailContext } from 'src/contexts/ticketDetail.context';
import { FlatItem } from 'src/types/elements.type';
import { IManpowerSupplyForm, IManpowerSupplyFormDetail } from 'src/types/helpdesk/manpowerSupplyForm.type';
import { HandleForRefParams, ITicket } from 'src/types/helpdesk/ticket.type';
import { formatDate, getOrderString } from 'src/utils/utils';
import usePermission from 'src/hooks/usePermission';
import './AddRelatedTicket.scss';
const { Text } = Typography;

interface Props {
  open: OpenRelatedTicketType;
  setOpen: React.Dispatch<React.SetStateAction<OpenRelatedTicketType>>;
}

type ItemListType = {
  id: string | number;
  ownerId: number;
  title: string | undefined;
  priorityLevel: FlatItem;
  createdDate: Date;
  details: IManpowerSupplyFormDetail[] | undefined;
};

const initHandleForRefParams = {
  page: 1,
  pageSize: 5,
  orderBy: getOrderString('createdDate', 'descend')
};

export default function AddRelatedTicket({ open, setOpen }: Props) {
  const [manpowerSupplyForms, setManpowerSupplyForms] = useState<IManpowerSupplyForm[]>([]);
  const [tickets, setTickets] = useState<ITicket[]>([]);
  const [employeeId, setEmployeeId] = useState<number | undefined>(undefined);
  const [ownerDepartmentId, setOwnerDepartmentId] = useState<number | undefined>(undefined);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const { ticket } = useContext(TicketDetailContext);
  const navigate = useNavigate();
  const { isUserAdmin } = useContext(AppContext);
  const [handleForRefParams, setHandleForRefParams] = useState<HandleForRefParams>(initHandleForRefParams);
  const [hrCategoryCodes, setHrCategoryCodes] = useState<string[]>([]);

  // Permission hooks for each ticket type
  const leaveTicketPermissions = usePermission({ path: urlPath.leaveTicket.location });
  const businessTripTicketPermissions = usePermission({ path: urlPath.businessTripTicket.location });
  const overtimeTicketPermissions = usePermission({ path: urlPath.overtimeTicket.location });
  const advancePaymentTicketPermissions = usePermission({ path: urlPath.advancePaymentTicket.location });
  const transportationTicketPermissions = usePermission({ path: urlPath.transportationTicket.location });
  const equipmentSupplyRequestTicketPermissions = usePermission({
    path: urlPath.equipmentSupplyRequestTicket.location
  });
  const purchaseRequestTicketPermissions = usePermission({ path: urlPath.purchaseRequestTicket.location });
  const additionalWorkingTimeTicketPermissions = usePermission({ path: urlPath.additionalWorkingTimeTicket.location });

  const handleCancel = () => {
    setOpen((prev) => ({
      ...prev,
      isOpen: false
    }));
    setTickets([]);
    setHandleForRefParams(initHandleForRefParams);
  };

  const hasExpiredDate = (manpowerSupplyForm: ItemListType) => {
    const dateMax: Date = handleGetMaxDate(manpowerSupplyForm.details as IManpowerSupplyFormDetail[]);
    const previousDate = new Date();
    previousDate?.setDate(previousDate?.getDate() - 1);
    if (Date.parse(dayjs(dateMax).format('YYYY-MM-DD')) < Date.parse(dayjs(previousDate).format('YYYY-MM-DD'))) {
      return true;
    } else {
      return false;
    }
  };

  const handleGetMinDate = (details: IManpowerSupplyFormDetail[]) => {
    return details
      ?.map((x) => x.fromDate)
      ?.reduce((a, b) => {
        return a < b ? a : b;
      }, new Date());
  };

  const handleGetMaxDate = (details: IManpowerSupplyFormDetail[]) => {
    return details
      ?.map((x) => x.toDate)
      ?.reduce((a, b) => {
        return a > b ? a : b;
      }, new Date());
  };

  useEffect(() => {
    if (ticket?.data) {
      setManpowerSupplyForms(ticket?.data.manpowerSupplyForms);
      setHrCategoryCodes(() => {
        const hrCategoryCodes = ticket?.data?.ticketCategory?.ticketCategoryMappings?.map(
          ({ hrTicketCategory }) => hrTicketCategory?.code
        );

        return hrCategoryCodes;
      });
    }
  }, [ticket?.data]);

  const handleLoadMore = () => {
    setHandleForRefParams((prev) => ({
      ...prev,
      page: prev.page + 1
    }));
  };

  // Call Api
  const myHandleForRefQuery = useQuery({
    queryKey: ['myHandleForRefQuery', handleForRefParams, employeeId, searchCondition],
    queryFn: () => {
      return getTicketsMyHandleForRef(handleForRefParams, employeeId, searchCondition);
    },
    staleTime: 60 * 1000,
    enabled: !!employeeId
  });

  useEffect(() => {
    if (isUserAdmin && myHandleForRefQuery.data?.data) {
      if (searchCondition) {
        setTickets(myHandleForRefQuery.data?.data.results);
      }
      setTickets((prev) => [...prev, ...myHandleForRefQuery.data?.data.results]);
    }
  }, [isUserAdmin, myHandleForRefQuery.data?.data, searchCondition]);

  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        if (!term) {
          setManpowerSupplyForms([]);
          setSearchCondition(term);
        } else {
          setSearchCondition(term);
        }
        setHandleForRefParams(initHandleForRefParams);
      }, 500),
    []
  );

  // Event handling
  const handleEmployeeChange = (value: string) => {
    const convertValue = value ? JSON.parse(value) : undefined;
    setEmployeeId(convertValue?.id);
    setOwnerDepartmentId(convertValue?.departmentId);
    setTickets([]);
    setHandleForRefParams(initHandleForRefParams);
  };

  const hrTickets = [
    {
      title: 'Tăng ca',
      description: 'Lập phiếu tăng ca',
      image: OvertimeIcon,
      code: HrCategoryEn.OT_TICKET,
      addUrl: urlPath.overtimeTicketAdd.location,
      hasPermissionCreate: overtimeTicketPermissions?.isCreate
    },
    {
      title: 'Nghỉ phép',
      description: 'Lập phiếu nghỉ phép',
      image: OvertimeIcon,
      code: HrCategoryEn.LV_TICKET,
      addUrl: urlPath.leaveTicketAdd.location,
      hasPermissionCreate: leaveTicketPermissions?.isCreate
    },
    {
      title: 'Công tác',
      description: 'Lập phiếu công tác',
      image: BusinessTripIcon,
      code: HrCategoryEn.BT_TICKET,
      addUrl: urlPath.businessTripTicketAdd.location,
      hasPermissionCreate: businessTripTicketPermissions?.isCreate
    },
    {
      title: 'Ứng tiền',
      description: 'Lập phiếu tạm ứng',
      image: MoneyIcon,
      code: HrCategoryEn.AP_TICKET,
      addUrl: urlPath.advancePaymentTicketAdd.location,
      hasPermissionCreate: advancePaymentTicketPermissions?.isCreate
    },
    {
      title: 'Vận chuyển',
      description: 'Lập phiếu vận chuyển',
      image: TruckIcon,
      code: HrCategoryEn.TRANS_TICKET,
      addUrl: urlPath.transportationTicketAdd.location,
      hasPermissionCreate: transportationTicketPermissions?.isCreate
    },
    {
      title: 'Yêu cầu cấp thiết bị',
      description: 'Lập phiếu yêu cầu cấp thiết bị',
      image: MaintenanceIcon,
      code: HrCategoryEn.ESR_TICKET,
      addUrl: urlPath.equipmentSupplyRequestTicketAdd.location,
      hasPermissionCreate: equipmentSupplyRequestTicketPermissions?.isCreate
    },
    {
      title: 'Yêu cầu mua hàng',
      description: 'Lập phiếu yêu cầu mua hàng',
      image: CartIcon,
      code: HrCategoryEn.PR_TICKET,
      addUrl: urlPath.purchaseRequestTicketAdd.location,
      hasPermissionCreate: purchaseRequestTicketPermissions?.isCreate
    },
    {
      title: 'Bổ sung giờ làm',
      description: 'Lập phiếu bổ sung giờ làm',
      image: WorkInProcessIcon,
      code: HrCategoryEn.AWT_PACKAGE,
      addUrl: urlPath.additionalWorkingTimeTicketAdd.location,
      hasPermissionCreate: additionalWorkingTimeTicketPermissions?.isCreate
    }
  ];

  return (
    <Modal
      title={
        (ticket?.data.manpowerSupplyForms && ticket?.data.manpowerSupplyForms.length > 0) || open.relatedTicketType
          ? 'Chọn phiếu cung ứng'
          : 'Chọn phiếu liên quan'
      }
      open={open.isOpen}
      width={600}
      maskClosable={false}
      onCancel={handleCancel}
      centered
      footer={null}
      styles={{ body: { marginTop: 20 } }}
    >
      {isUserAdmin && open.relatedTicketType ? (
        <div className='related-ticket'>
          <EmployeeGroupSelect
            style={{ width: '100%' }}
            onChange={handleEmployeeChange}
            placeholder='Lọc ticket theo nhân viên'
            allowClear
          />
          <Input
            placeholder='Nhập mã ticket hoặc mã dự án'
            allowClear
            style={{ marginTop: 16 }}
            suffix={
              <SearchOutlined
                style={{
                  fontSize: 16,
                  color: '#1677ff'
                }}
              />
            }
            disabled={!employeeId}
            onChange={(e) => debouncedSearch(e.target.value)}
          />
          <InfiniteScroll
            className='related-ticket-body'
            height='auto'
            dataLength={tickets?.length}
            hasMore={
              Number(myHandleForRefQuery.data?.data.pageNumber) < Number(myHandleForRefQuery.data?.data.totalPages)
            }
            loader={<p style={{ textAlign: 'center', padding: 20 }}>Loading...</p>}
            next={handleLoadMore}
          >
            <List
              dataSource={tickets}
              locale={{
                emptyText: (
                  <Empty
                    image='https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg'
                    imageStyle={{ height: 100, marginBlockStart: 16 }}
                    description={<Text type='secondary'>Không có kết quả được tìm thấy !</Text>}
                  />
                )
              }}
              loading={myHandleForRefQuery.isFetching}
              renderItem={(item) => (
                <List
                  header={<strong>Mã Ticket: {item?.ticketCode}</strong>}
                  className='manpower-supply-form-list'
                  itemLayout='horizontal'
                  dataSource={item.manpowerSupplyForms.map(
                    (data) =>
                      ({
                        id: data.id,
                        ownerId: employeeId,
                        title: data.projectCode,
                        priorityLevel: data.priorityLevel,
                        createdDate: data.createdDate,
                        details: data.details
                      } as ItemListType)
                  )}
                  renderItem={(itemChild) => (
                    <List.Item
                      key={itemChild.id}
                      className={`manpower-supply-item-wrapper  ${
                        hasExpiredDate(itemChild) && !isUserAdmin ? 'expired' : ''
                      }`}
                      onClick={() => {
                        switch (open.relatedTicketType) {
                          case 'overTime':
                            navigate(
                              `${urlPath.overtimeTicketAdd.location}?${ILinkId.manpowerSupplyFormId}=${itemChild.id}&ownerId=${employeeId}&ownerDepartmentId=${ownerDepartmentId}`
                            );
                            break;
                          case 'business':
                            navigate(
                              `${urlPath.businessTripTicketAdd.location}?${ILinkId.manpowerSupplyFormId}=${itemChild.id}&ownerId=${employeeId}`
                            );
                            break;
                          default:
                            break;
                        }
                      }}
                    >
                      <Flex className='manpower-supply-item ' style={{ width: '100%' }} vertical>
                        <Flex style={{ width: '100%' }} className='item-select'>
                          <List.Item.Meta
                            title={
                              <Text strong>
                                ID: {itemChild.id} - <span>Dự án: {itemChild.title ?? ''}</span>
                              </Text>
                            }
                            description={
                              <Flex
                                vertical
                                gap={6}
                                style={{
                                  marginTop: '10px'
                                }}
                              >
                                <Text>
                                  <Text type='secondary'>Kéo dài từ ngày: </Text>
                                  {formatDate(handleGetMinDate(itemChild.details || []))}{' '}
                                  <Text type='secondary'>đến </Text>{' '}
                                  {formatDate(handleGetMaxDate(itemChild.details || []))}
                                </Text>
                                {hasExpiredDate(itemChild) && !isUserAdmin && (
                                  <Tag color='#f50' style={{ width: 'fit-content' }}>
                                    Đã hết hạn thực hiện
                                  </Tag>
                                )}
                              </Flex>
                            }
                          />
                          <Flex gap={5}>
                            <PriorityLevelTag priorityLevel={itemChild.priorityLevel} />
                            {hasExpiredDate(itemChild) && !isUserAdmin && (
                              <Icon
                                url={Expired}
                                className='icon-expired'
                                size={20}
                                disable={true}
                                titleTooltip='Hết hạn'
                                arrowTooltip={false}
                              />
                            )}
                          </Flex>
                        </Flex>
                      </Flex>
                    </List.Item>
                  )}
                />
              )}
            />
          </InfiniteScroll>
        </div>
      ) : ticket?.data.manpowerSupplyForms && ticket?.data.manpowerSupplyForms.length > 0 ? (
        <Fragment>
          <Input
            placeholder='Nhập mã phiếu hoặc mã dự án'
            size='middle'
            allowClear
            suffix={
              <SearchOutlined
                style={{
                  fontSize: 16,
                  color: '#1677ff'
                }}
              />
            }
            onChange={(e) => {
              const value = e.target.value;
              if (!value) setManpowerSupplyForms(ticket?.data.manpowerSupplyForms);
              else {
                const newManpowerSupplyForms = ticket?.data.manpowerSupplyForms.filter((item) => {
                  return (
                    item?.projectCode?.toLowerCase()?.includes(value.toLowerCase()) ||
                    item?.id?.toString()?.toLowerCase()?.includes(value.toLowerCase())
                  );
                });
                setManpowerSupplyForms(newManpowerSupplyForms);
              }
            }}
          />
          <div className='related-ticket-body'>
            <List
              itemLayout='horizontal'
              className='manpower-supply-form-list'
              header={<strong>Mã Ticket: {ticket?.data?.ticketCode}</strong>}
              dataSource={manpowerSupplyForms.map(
                (data) =>
                  ({
                    id: data.id,
                    title: data.projectCode,
                    priorityLevel: data.priorityLevel,
                    createdDate: data.createdDate,
                    details: data.details
                  } as ItemListType)
              )}
              renderItem={(item, index) => (
                <List.Item
                  key={item.id}
                  className={`manpower-supply-item-wrapper hover ${hasExpiredDate(item) ? 'expired' : ''}`}
                >
                  <Flex className='manpower-supply-item ' style={{ width: '100%' }} vertical>
                    <Flex style={{ width: '100%' }} className='item-select'>
                      <List.Item.Meta
                        title={
                          <Text strong>
                            ID: {item.id} - <span>Dự án: {item.title ?? ''}</span>
                          </Text>
                        }
                        description={
                          <Flex
                            vertical
                            gap={6}
                            style={{
                              marginTop: '10px'
                            }}
                          >
                            <Text>
                              <Text type='secondary'>Kéo dài từ ngày: </Text>
                              {formatDate(handleGetMinDate(item.details || []))} <Text type='secondary'>đến </Text>{' '}
                              {formatDate(handleGetMaxDate(item.details || []))}
                            </Text>
                            {hasExpiredDate(item) && (
                              <Tag color='#f50' style={{ width: 'fit-content' }}>
                                Đã hết hạn thực hiện
                              </Tag>
                            )}
                          </Flex>
                        }
                      />
                      <Flex gap={5}>
                        <PriorityLevelTag priorityLevel={item.priorityLevel} />
                        {hasExpiredDate(item) && (
                          <Icon
                            url={Expired}
                            className='icon-expired'
                            size={20}
                            disable={true}
                            titleTooltip='Hết hạn'
                            arrowTooltip={false}
                          />
                        )}
                      </Flex>
                    </Flex>
                    {!hasExpiredDate(item) && (
                      <Flex gap={15} className='list-btn'>
                        <CustomCard
                          image={OvertimeIcon}
                          imageSize={64}
                          title='Tăng ca'
                          // disable={!permissionCreateOT}
                          description='Lập phiếu tăng ca'
                          layout='horizontal'
                          action={null}
                          size='medium'
                          style={{ width: '100%', minWidth: 'unset' }}
                          hoverable
                          onClick={() =>
                            navigate(`${urlPath.overtimeTicketAdd.location}?${ILinkId.manpowerSupplyFormId}=${item.id}`)
                          }
                        />
                        <CustomCard
                          image={BusinessTripIcon}
                          imageSize={64}
                          disable={!businessTripTicketPermissions?.isCreate}
                          title='Công tác'
                          description='Lập phiếu công tác'
                          layout='horizontal'
                          action={null}
                          size='medium'
                          style={{ width: '100%', minWidth: 'unset' }}
                          hoverable
                          onClick={() =>
                            navigate(
                              `${urlPath.businessTripTicketAdd.location}?${ILinkId.manpowerSupplyFormId}=${item.id}`
                            )
                          }
                        />
                      </Flex>
                    )}
                  </Flex>
                </List.Item>
              )}
            />
          </div>
        </Fragment>
      ) : (
        <Row justify='space-evenly' className='card-row' gutter={[20, 20]}>
          {hrTickets.map((hrTicket) => {
            if (hrCategoryCodes.includes(hrTicket.code)) {
              return (
                <Col span={24} key={hrTicket.code}>
                  <CustomCard
                    image={hrTicket.image}
                    imageSize={64}
                    title={hrTicket.title}
                    description={hrTicket.description}
                    layout='horizontal'
                    disable={!hrTicket.hasPermissionCreate}
                    action={null}
                    size='medium'
                    style={{ width: '100%' }}
                    onClick={() =>
                      navigate(
                        `${hrTicket.addUrl}?${ILinkId.refTicketId}=${open.ticketId}&${ILinkId.manpowerSupplyFormId}=${open.manpowerId}`
                      )
                    }
                    hoverable
                  />
                </Col>
              );
            }
            return <Fragment key={hrTicket.code} />;
          })}
        </Row>
      )}
    </Modal>
  );
}
